import { getCreateSiteUrl, getUpdateSiteUrl } from 'generatedTypes/client/services/site-controller';
import { getAssignSiteToBoundaryUrl } from 'generatedTypes/flight/services/boundary-controller';
import { useState } from 'react';

import useFetch from 'hooks/useFetch';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import { SITES_API } from './constants';
import type {
    AssignFieldToBoundary,
    CreateField,
    FetchField,
    FetchFieldHierarchyFormItems,
    SiteResponse,
    SiteWithParcelAndClientResponse,
    UpdateField
} from './types';

const useField = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { authenticatedGet, authenticatedPost, authenticatedPut } = useFetch();
    const { withRetry } = useRetry();
    const { CONNECTION_ERROR, SAVE_FAILED } = useTranslation();

    const fetchField: FetchField = async ({ id }) => {
        try {
            setIsLoading(true);

            return await withRetry(
                () => authenticatedGet<SiteWithParcelAndClientResponse>(`${SITES_API}/${id}`),
                CONNECTION_ERROR,
                () => fetchField({ id })
            );
        } finally {
            setIsLoading(false);
        }
    };

    const createField: CreateField = async ({ farmId, field }) => {
        try {
            setIsLoading(true);
            const url = getCreateSiteUrl(farmId);

            return await withRetry(
                () => authenticatedPost<SiteResponse>(url, JSON.stringify(field)),
                SAVE_FAILED,
                () => createField({ farmId, field })
            );
        } finally {
            setIsLoading(false);
        }
    };

    const updateField: UpdateField = async ({ field, id }) => {
        try {
            setIsLoading(true);
            const url = getUpdateSiteUrl(id);

            return await withRetry(
                () => authenticatedPut<SiteResponse>(url, JSON.stringify(field)),
                SAVE_FAILED,
                () => updateField({ field, id })
            );
        } finally {
            setIsLoading(false);
        }
    };

    // The endpoint answers 204 with no body, so success is signalled by the absence of an
    // error rather than by returned data.
    const assignFieldToBoundary: AssignFieldToBoundary = async ({ boundaryId, siteId }) => {
        try {
            setIsLoading(true);
            const url = getAssignSiteToBoundaryUrl(boundaryId, siteId);

            const result = await withRetry(
                async () => {
                    const { error } = await authenticatedPut(url, null);

                    return { data: error ? null : true, error };
                },
                SAVE_FAILED,
                () => assignFieldToBoundary({ boundaryId, siteId })
            );

            return result !== null;
        } finally {
            setIsLoading(false);
        }
    };

    const fetchFieldHierarchyFormItems: FetchFieldHierarchyFormItems = async ({ id }) => {
        const fieldData = await fetchField({ id });

        if (!fieldData) {
            return null;
        }

        return {
            client: {
                label: fieldData.clientName ?? '',
                value: fieldData.clientId ?? ''
            },
            farm: {
                label: fieldData.parcelName ?? '',
                value: fieldData.parcelId ?? ''
            },
            field: {
                label: fieldData.name ?? '',
                value: fieldData.id ?? ''
            }
        };
    };

    return {
        assignFieldToBoundary,
        createField,
        fetchField,
        fetchFieldHierarchyFormItems,
        isLoading,
        updateField
    };
};

export default useField;
